/**
 * Public `/website/:username/*` path ↔ template page state.
 * URL segment `content` maps to the template Content page (`PAGES.content`).
 */

import type { TemplateCarouselDeepLink, TemplateContentView, TemplateShopView, ShopBrowseSelection, Tweaks } from "./types";
import {
  CUSTOM_PAGE_PREFIX,
  findCustomWebsitePage,
  findCustomWebsitePageByPublicSlug,
  isCustomWebsitePageKey,
} from "./customWebsitePages";
import {
  builtinPagePublicRoot,
  rewritePublicPathForBuiltinSlugAliases,
} from "./builtinPagePublicSlug";
import { coerceShopBrowseSelection } from "./shopBreadcrumbs";
import { isContentPlatformBrowseSlug } from "./websiteEditorInspectorTypes";
import { isPublicWebsiteTemplateRoute } from "../../../Utils/websiteTemplateGuards";
import type { CreatorBrowsableUrlPrefs } from "../../../Utils/url/getCreatorPublicBaseUrl";
import {
  isLocalDevHost,
  isVercelDeploymentHost,
} from "../../../Utils/url/creatorHost";
import {
  getSelectedCreatorPublicBaseUrl,
  resolveSelectedCreatorUrlPrefs,
} from "../../../Utils/url/getSelectedCreatorPublicUrl";

export {
  builtinPagePublicRoot,
  matchBuiltinPageKeyByPublicRoot,
  rewritePublicPathForBuiltinSlugAliases,
} from "./builtinPagePublicSlug";

export type WebsiteShareUrlOpts = {
  customDomain?: string | null;
  browsableUrlPrefs?: CreatorBrowsableUrlPrefs | null;
};

function stripHost(value: string): string {
  return String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
}

function isBrowsableApex(domain: string): boolean {
  const d = domain.toLowerCase();
  return d === "browsable.ai" || d === "browsable.bio";
}

/** Localhost / Vercel — keep share links on the current origin for preview testing. */
function shouldUseCurrentOriginForShare(): boolean {
  if (typeof window === "undefined") return false;
  return isLocalDevHost() || isVercelDeploymentHost();
}

function currentOriginShareBase(path: string): string {
  const normalized = `/${String(path || "").replace(/^\/+/, "")}`;
  if (typeof window === "undefined") return normalized;
  return `${window.location.origin.replace(/\/+$/, "")}${normalized}`;
}

/**
 * Absolute share URL for a published website.
 * Localhost/Vercel: current origin + /{username} (local testing).
 * Otherwise: DomainControls selection (custom domain → url_type+domain).
 */
export function getWebsiteTemplateShareUrl(
  username: string,
  opts?: WebsiteShareUrlOpts,
): string {
  const slug = String(username || "").trim();
  if (!slug) return "";
  if (shouldUseCurrentOriginForShare()) {
    return currentOriginShareBase(`/${encodeURIComponent(slug.toLowerCase())}`);
  }
  return getSelectedCreatorPublicBaseUrl({
    username: slug,
    customDomain: opts?.customDomain,
    browsableUrlPrefs: opts?.browsableUrlPrefs,
  });
}

/**
 * Public demo URL — `/website/:username` (or `/website` on host-style bases)
 * so LiB stays live at the selected public home.
 * Localhost/Vercel: current origin + /website/{username}.
 * Otherwise: DomainControls selection.
 */
export function getWebsiteDemoShareUrl(
  username: string,
  opts?: WebsiteShareUrlOpts,
): string {
  const slug = String(username || "").trim().toLowerCase();
  if (!slug) return "";

  if (shouldUseCurrentOriginForShare()) {
    return currentOriginShareBase(`/website/${encodeURIComponent(slug)}`);
  }

  const activeCustom = stripHost(String(opts?.customDomain || ""));
  if (activeCustom) {
    return `https://${activeCustom}/website`;
  }

  const { urlType, domain } = resolveSelectedCreatorUrlPrefs(
    slug,
    opts?.browsableUrlPrefs,
  );

  if (domain && !isBrowsableApex(domain)) {
    return `https://${domain}/website`;
  }

  const apex = isBrowsableApex(domain) ? domain : "browsable.ai";
  if (urlType === "subdomain") {
    return `https://${encodeURIComponent(slug)}.${apex}/website`;
  }
  return `https://${apex}/website/${encodeURIComponent(slug)}`;
}

export type ParsedPublicWebsitePath = {
  pageKey: string;
  blogPostSlug?: string;
  platformKey?: string;
  carouselDeepLink?: TemplateCarouselDeepLink;
  /** Stay Up To Date component slug under `/link-in-bio/:slug` (or `contact-me`). */
  linkInBioComponentId?: string;
  /** Product-bank collection unique_id under `/shop/collections/:id`. */
  productCollectionId?: string;
  /**
   * Path matches no built-in website route. A single unknown segment is not
   * unrecognized — it may be a creator page or a legacy collection link.
   */
  unrecognized?: boolean;
};

function decodeSeg(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Resolve `template_analytics.page_key` from a public website pathname.
 * Supports `/website/:username/*` and canonical `/{username}/*`.
 */
export function resolveTemplatePageKeyFromPathname(
  pathname?: string,
): string | undefined {
  const p =
    pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "");
  const websiteMatch = p.match(/^\/website\/[^/]+\/?(.*)$/i);
  if (websiteMatch) {
    return parsePublicWebsiteSubpath(websiteMatch[1] || "").pageKey;
  }
  if (!isPublicWebsiteTemplateRoute(p)) return undefined;
  const canonicalMatch = p.match(/^\/[^/]+\/?(.*)$/i);
  if (canonicalMatch) {
    return parsePublicWebsiteSubpath(canonicalMatch[1] || "").pageKey;
  }
  return undefined;
}

/** Parse the segment string matched by React Router `*` under `/website/:username/`. */
export function parsePublicWebsiteSubpath(rest: string | undefined): ParsedPublicWebsitePath {
  const raw = (rest ?? "").replace(/^\/+/, "").replace(/\/+$/, "");
  const seg = raw.split("/").filter(Boolean);
  if (!seg.length) return { pageKey: "home" };

  const a = decodeSeg(seg[0]).toLowerCase();
  const b = seg[1];

  if (a === "blog" && b) {
    return { pageKey: "journalPost", blogPostSlug: decodeSeg(b) };
  }
  if (a === "blog") return { pageKey: "journal" };

  // Legacy `/platform/:key` — Latest Content was never a site page; open Content.
  if (a === "platform" && b) {
    const platformSlug = decodeSeg(b);
    return {
      pageKey: "content",
      carouselDeepLink: {
        target: "content",
        slug: platformSlug,
        shopBrowse: {
          primarySlug: platformSlug,
          secondarySlug: null,
          brandSlug: null,
        },
      },
    };
  }

  if (a === "shop" && b === "trending") {
    return {
      pageKey: "shop",
      carouselDeepLink: { target: "shop", shopView: "trending" },
    };
  }
  if (a === "shop" && b === "brand" && seg[2]) {
    // Single: /shop/brand/innbeauty
    // Multi (Featured brands Shop): /shop/brand/a+b+c
    const brandSeg = decodeSeg(seg[2]);
    const brandSlugs = brandSeg
      .split(/[+|,]/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .slice(0, 4);
    const brandSlug = brandSlugs[0] || brandSeg.trim().toLowerCase();
    return {
      pageKey: "shop",
      carouselDeepLink: {
        target: "shop",
        shopView: "brand",
        shopBrowse: {
          brandSlug: brandSlug || null,
          ...(brandSlugs.length > 1 ? { brandSlugs } : {}),
        },
      },
    };
  }
  if (a === "shop" && b === "content") {
    const platformSlug = seg[2] ? decodeSeg(seg[2]) : undefined;
    const formatSlug = seg[3] ? decodeSeg(seg[3]) : undefined;
    return {
      pageKey: "content",
      carouselDeepLink: {
        target: "content",
        ...(platformSlug
          ? {
              slug: platformSlug,
              shopBrowse: {
                primarySlug: platformSlug,
                secondarySlug: formatSlug ?? null,
                brandSlug: null,
              },
            }
          : {}),
      },
    };
  }
  if (a === "shop" && b === "category" && seg[2]) {
    const catSlug = decodeSeg(seg[2]);
    // Optional: /shop/category/{cat}/{primary}/{secondary}/{sub}/{brand}
    const primarySlug = seg[3] ? decodeSeg(seg[3]) : undefined;
    const secondarySlug = seg[4] ? decodeSeg(seg[4]) : undefined;
    const subSlug = seg[5] ? decodeSeg(seg[5]) : undefined;
    const brandSlug = seg[6] ? decodeSeg(seg[6]) : undefined;
    return {
      pageKey: "shop",
      carouselDeepLink: {
        target: "shop",
        shopView: "category",
        slug: catSlug,
        ...(primarySlug
          ? {
              shopBrowse: {
                primarySlug,
                secondarySlug: secondarySlug ?? null,
                subSlug: subSlug ?? null,
                brandSlug: brandSlug ?? null,
              },
            }
          : {}),
      },
    };
  }
  if (a === "shop" && b === "collections" && seg[2]) {
    const productCollectionId = decodeSeg(seg[2]);
    return {
      pageKey: "shop",
      productCollectionId,
      carouselDeepLink: {
        target: "shop",
        shopView: "all",
        productCollectionId,
      },
    };
  }
  if (a === "shop" && b === "collection" && seg[2]) {
    return {
      pageKey: "shop",
      carouselDeepLink: {
        target: "shop",
        shopView: "all",
        slug: decodeSeg(seg[2]),
      },
    };
  }
  if (a === "shop" && b && b !== "category") {
    const primarySlug = decodeSeg(b);
    const secondarySlug = seg[2] ? decodeSeg(seg[2]) : undefined;
    // 3rd segment is subcategory (was historically stored as brandSlug).
    const subSlug = seg[3] ? decodeSeg(seg[3]) : undefined;
    const brandSlug = seg[4] ? decodeSeg(seg[4]) : undefined;
    // `/{user}/shop/{id}` is a product carousel (LiB / day-window), not a
    // category. Multi-segment paths stay browse (`/shop/{primary}/{secondary}`).
    if (!secondarySlug) {
      return {
        pageKey: "shop",
        carouselDeepLink: {
          target: "shop",
          shopView: "all",
          slug: primarySlug,
        },
      };
    }
    return {
      pageKey: "shop",
      carouselDeepLink: {
        target: "shop",
        shopView: "all",
        shopBrowse: {
          primarySlug,
          ...(secondarySlug ? { secondarySlug } : {}),
          ...(subSlug ? { subSlug } : {}),
          ...(brandSlug ? { brandSlug } : {}),
        },
      },
    };
  }
  if (a === "shop") {
    return {
      pageKey: "shop",
      carouselDeepLink: { target: "shop", shopView: "all" },
    };
  }

  if (a === "content" && b === "series") {
    const seriesSlug = seg[2] ? decodeSeg(seg[2]) : undefined;
    return {
      pageKey: "content",
      carouselDeepLink: {
        target: "content",
        contentView: "series",
        ...(seriesSlug ? { slug: seriesSlug } : {}),
      },
    };
  }

  if (a === "content" && b) {
    const platformSlug = decodeSeg(b);
    const formatSlug = seg[2] ? decodeSeg(seg[2]) : undefined;
    // Two segments = platform + format browse. One segment may be either a
    // content carousel slug or a platform — known platforms always browse.
    if (formatSlug) {
      return {
        pageKey: "content",
        carouselDeepLink: {
          target: "content",
          slug: platformSlug,
          shopBrowse: {
            primarySlug: platformSlug,
            secondarySlug: formatSlug,
            brandSlug: null,
          },
        },
      };
    }
    if (isContentPlatformBrowseSlug(platformSlug)) {
      return {
        pageKey: "content",
        carouselDeepLink: {
          target: "content",
          slug: platformSlug,
          shopBrowse: {
            primarySlug: platformSlug,
            secondarySlug: null,
            brandSlug: null,
          },
        },
      };
    }
    return {
      pageKey: "content",
      carouselDeepLink: {
        target: "content",
        slug: platformSlug,
      },
    };
  }
  if (a === "content") {
    return {
      pageKey: "content",
      carouselDeepLink: { target: "content", contentView: "latest" },
    };
  }

  if (a === "link-in-bio") {
    if (!b) return { pageKey: "linkInBio" };
    return { pageKey: "linkInBio", linkInBioComponentId: decodeSeg(b) };
  }

  // Classic LiB Contact Me is `/contact-me`. Website Contact lives at `/contact`.
  // Without this alias, `/{user}/contact-me` becomes `c_contact-me`, fails legacy
  // component lookup, then flips to TemplateNotFoundPage after a short delay.
  if (a === "contact-me" && !b) return { pageKey: "contact" };

  const single = new Set(["about", "contact", "press", "newsletter", "search"]);
  if (single.has(a) && !b) return { pageKey: a };

  if (a && !b) {
    return { pageKey: `c_${a}` };
  }

  return { pageKey: "home", unrecognized: true };
}

/** Template page key for an unrecognized public route. */
export const NOT_FOUND_PAGE_KEY = "notFound";

/** Public route class for `/{username}/*` after custom pages are known. */
export type PublicWebsiteRouteStatus = "page" | "legacy-candidate" | "not-found";

export type ResolvedPublicWebsiteRoute = {
  status: PublicWebsiteRouteStatus;
  /** Path rest the template should render. Custom pages use their stored id slug. */
  pathRest: string;
};

/**
 * Built-in routes (home, shop, blog, …) and creator-made pages are real.
 * One unknown segment may still be an old `/{username}/{collectionId}` link.
 * Anything else is not a page.
 */
export function resolvePublicWebsiteRoute(
  rest: string | undefined,
  tweaks?: Tweaks | null,
): ResolvedPublicWebsiteRoute {
  const rawPath = String(rest || "").replace(/^\/+/, "").replace(/\/+$/, "");
  // Renamed built-ins (e.g. Content → /hello-ganika) must rewrite before the
  // `c_*` custom-page fallback — otherwise the slug 404s while /content still works.
  const pathRest = rewritePublicPathForBuiltinSlugAliases(rawPath, tweaks);
  const parsed = parsePublicWebsiteSubpath(pathRest);
  if (parsed.unrecognized) return { status: "not-found", pathRest };
  if (!isCustomWebsitePageKey(parsed.pageKey)) return { status: "page", pathRest };

  const slug = parsed.pageKey.slice(CUSTOM_PAGE_PREFIX.length);
  if (!tweaks) return { status: "legacy-candidate", pathRest: rawPath };
  const page = findCustomWebsitePageByPublicSlug(tweaks, slug);
  if (!page) return { status: "legacy-candidate", pathRest: rawPath };
  const idSlug = page.id.startsWith(CUSTOM_PAGE_PREFIX)
    ? page.id.slice(CUSTOM_PAGE_PREFIX.length)
    : slug;
  return { status: "page", pathRest: idSlug };
}

/**
 * Route string for `getCreatorSpaRelativePath({ route })` (no leading slash).
 * `undefined` means creator home (`/website/:username` or domain-relative `/`).
 */
export function publicPathRestForTemplatePage(
  pageKey: string,
  opts?: {
    carouselSlug?: string;
    postSlugOrId?: string;
    platformKey?: string;
    contentView?: TemplateContentView;
    shopView?: TemplateShopView;
    categorySlug?: string;
    shopBrowse?: ShopBrowseSelection;
    /** Product-bank collection unique_id (`/shop/collections/:id`). */
    productCollectionId?: string;
    linkInBioComponentId?: string;
    /** When set, custom pages (`c_*`) use the live `customPages[].slug`. */
    tweaks?: Tweaks | null;
  },
): string | undefined {
  if (pageKey === "home") return undefined;

  if (pageKey === "journalPost" && opts?.postSlugOrId?.trim()) {
    const blogRoot =
      builtinPagePublicRoot(opts?.tweaks, "journal") || "blog";
    return `${blogRoot}/${encodeURIComponent(opts.postSlugOrId.trim())}`;
  }
  if (pageKey === "journal") {
    return builtinPagePublicRoot(opts?.tweaks, "journal") || "blog";
  }

  if (pageKey === "platform" && opts?.platformKey?.trim()) {
    return `content/${encodeURIComponent(opts.platformKey.trim())}`;
  }

  if (pageKey === "shop") {
    const root = builtinPagePublicRoot(opts?.tweaks, "shop") || "shop";
    const productCollectionId = opts?.productCollectionId?.trim();
    if (productCollectionId) {
      return `${root}/collections/${encodeURIComponent(productCollectionId)}`;
    }
    const view = opts?.shopView ?? "all";
    if (view === "trending") return `${root}/trending`;
    if (view === "brand") {
      const multi = (opts?.shopBrowse?.brandSlugs || [])
        .map((s) => String(s || "").trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 4);
      const single = opts?.shopBrowse?.brandSlug?.trim().toLowerCase();
      const brands = multi.length ? multi : single ? [single] : [];
      if (!brands.length) return root;
      return `${root}/brand/${brands.map((s) => encodeURIComponent(s)).join("+")}`;
    }
    if (view === "content") {
      const platform =
        opts?.categorySlug?.trim() || opts?.shopBrowse?.primarySlug?.trim();
      const format = opts?.shopBrowse?.secondarySlug?.trim();
      if (platform && format) {
        return `${root}/content/${encodeURIComponent(platform)}/${encodeURIComponent(format)}`;
      }
      if (platform) return `${root}/content/${encodeURIComponent(platform)}`;
      return `${root}/content`;
    }
    if (view === "category") {
      const s = opts?.categorySlug?.trim() || opts?.carouselSlug?.trim();
      if (!s) return root;
      const browse = opts?.shopBrowse;
      const primary = browse?.primarySlug?.trim();
      if (primary) {
        const { secondarySlug, subSlug, brandSlug } = coerceShopBrowseSelection(
          browse ?? {},
        );
        const secondary = secondarySlug || undefined;
        const sub = subSlug || undefined;
        const brand = brandSlug || undefined;
        if (secondary && sub && brand) {
          return `${root}/category/${encodeURIComponent(s)}/${encodeURIComponent(primary)}/${encodeURIComponent(secondary)}/${encodeURIComponent(sub)}/${encodeURIComponent(brand)}`;
        }
        if (secondary && sub) {
          return `${root}/category/${encodeURIComponent(s)}/${encodeURIComponent(primary)}/${encodeURIComponent(secondary)}/${encodeURIComponent(sub)}`;
        }
        if (secondary) {
          return `${root}/category/${encodeURIComponent(s)}/${encodeURIComponent(primary)}/${encodeURIComponent(secondary)}`;
        }
        return `${root}/category/${encodeURIComponent(s)}/${encodeURIComponent(primary)}`;
      }
      return `${root}/category/${encodeURIComponent(s)}`;
    }
    const browse = opts?.shopBrowse;
    const primary = browse?.primarySlug?.trim();
    if (primary) {
      const { secondarySlug, subSlug, brandSlug } = coerceShopBrowseSelection(
        browse ?? {},
      );
      const secondary = secondarySlug || undefined;
      const sub = subSlug || undefined;
      const brand = brandSlug || undefined;
      if (secondary && sub && brand) {
        return `${root}/${encodeURIComponent(primary)}/${encodeURIComponent(secondary)}/${encodeURIComponent(sub)}/${encodeURIComponent(brand)}`;
      }
      if (secondary && sub) {
        return `${root}/${encodeURIComponent(primary)}/${encodeURIComponent(secondary)}/${encodeURIComponent(sub)}`;
      }
      if (secondary) {
        return `${root}/${encodeURIComponent(primary)}/${encodeURIComponent(secondary)}`;
      }
      return `${root}/${encodeURIComponent(primary)}`;
    }
    const collectionSlug = opts?.carouselSlug?.trim();
    if (view === "all" && collectionSlug) {
      return encodeURIComponent(collectionSlug);
    }
    return root;
  }

  if (pageKey === "content") {
    const root =
      builtinPagePublicRoot(opts?.tweaks, "content") || "content";
    const browsePrimary = opts?.shopBrowse?.primarySlug?.trim();
    const format = opts?.shopBrowse?.secondarySlug?.trim();
    const carouselSlug = opts?.carouselSlug?.trim();
    if (opts?.contentView === "series") {
      if (
        carouselSlug &&
        !browsePrimary &&
        !isContentPlatformBrowseSlug(carouselSlug)
      ) {
        return encodeURIComponent(carouselSlug);
      }
      return carouselSlug
        ? `${root}/series/${encodeURIComponent(carouselSlug)}`
        : `${root}/series`;
    }
    if (browsePrimary && format) {
      return `${root}/${encodeURIComponent(browsePrimary)}/${encodeURIComponent(format)}`;
    }
    if (browsePrimary) return `${root}/${encodeURIComponent(browsePrimary)}`;
    if (carouselSlug && isContentPlatformBrowseSlug(carouselSlug)) {
      return `${root}/${encodeURIComponent(carouselSlug)}`;
    }
    if (carouselSlug) return encodeURIComponent(carouselSlug);
    return root;
  }

  if (pageKey === "linkInBio") {
    const root =
      builtinPagePublicRoot(opts?.tweaks, "linkInBio") || "link-in-bio";
    const slug = opts?.linkInBioComponentId?.trim();
    return slug ? `${root}/${encodeURIComponent(slug)}` : root;
  }

  if (["about", "contact", "press", "newsletter", "search"].includes(pageKey)) {
    return builtinPagePublicRoot(opts?.tweaks, pageKey) || pageKey;
  }

  if (pageKey.startsWith("c_")) {
    // Prefer the editable public slug — id is frozen at create time (`c_${firstSlug}`).
    const custom = opts?.tweaks
      ? findCustomWebsitePage(opts.tweaks, pageKey)
      : undefined;
    const live = String(custom?.slug || "").trim();
    return live || pageKey.slice(2);
  }

  return undefined;
}
